import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { ExternalLink, Twitter, Linkedin, Github, Globe, Mail, Phone, Instagram, Youtube, Music, MapPin, QrCode, Facebook, MessageCircle, Play, Pause, ArrowUpRight, UserPlus } from "lucide-react";
import api, { BACKEND_URL } from "@/lib/api";
import SEO from "@/components/SEO";

const ICON_MAP = {
  twitter: Twitter, linkedin: Linkedin, github: Github, globe: Globe,
  mail: Mail, email: Mail, phone: Phone, instagram: Instagram,
  youtube: Youtube, music: Music, location: MapPin, facebook: Facebook,
  whatsapp: MessageCircle, message: MessageCircle, 'external-link': ExternalLink,
};

function LinkIcon({ icon }) {
  const Icon = ICON_MAP[icon?.toLowerCase()] || ExternalLink;
  return <Icon size={18} strokeWidth={1.8} />;
}

const KEYFRAMES = `
@keyframes ppFade { from { opacity:0 } to { opacity:1 } }
@keyframes ppSlideL { from { opacity:0; transform:translateX(-60px) } to { opacity:1; transform:translateX(0) } }
@keyframes ppSlideR { from { opacity:0; transform:translateX(60px) } to { opacity:1; transform:translateX(0) } }
@keyframes ppSlideU { from { opacity:0; transform:translateY(50px) } to { opacity:1; transform:translateY(0) } }
@keyframes ppSlideD { from { opacity:0; transform:translateY(-50px) } to { opacity:1; transform:translateY(0) } }
@keyframes ppZoom { from { opacity:0; transform:scale(0.7) } to { opacity:1; transform:scale(1) } }
@keyframes ppBounceIn { 
  0% { opacity:0; transform:scale(0.3) } 
  50% { opacity:1; transform:scale(1.08) } 
  70% { transform:scale(0.95) } 
  100% { transform:scale(1) } 
}
@keyframes ppRotate { from { opacity:0; transform:rotate(-12deg) scale(0.8) } to { opacity:1; transform:rotate(0) scale(1) } }
@keyframes ppFlip { from { opacity:0; transform:perspective(800px) rotateY(90deg) } to { opacity:1; transform:perspective(800px) rotateY(0) } }
@keyframes ppFlipX { from { opacity:0; transform:perspective(800px) rotateX(90deg) } to { opacity:1; transform:perspective(800px) rotateX(0) } }
@keyframes ppFloat { 0%,100% { transform:translateY(0) } 50% { transform:translateY(-8px) } }
@keyframes ppPulse { 0%,100% { transform:scale(1) } 50% { transform:scale(1.03) } }
@keyframes ppGlow { 0%,100% { box-shadow: 0 0 20px rgba(99,102,241,0.2) } 50% { box-shadow: 0 0 30px rgba(99,102,241,0.4) } }
@keyframes ppShine { from { background-position: -200% center } to { background-position: 200% center } }
@keyframes ppPulseRing { 0% { box-shadow: 0 0 0 0 rgba(99,102,241,0.4) } 70% { box-shadow: 0 0 0 16px rgba(99,102,241,0) } 100% { box-shadow: 0 0 0 0 rgba(99,102,241,0) } }
@keyframes ppEnter { from { opacity:0; transform:translateY(30px) } to { opacity:1; transform:translateY(0) } }
@keyframes ppAvatarPop { 
  0% { opacity:0; transform:scale(0.5) } 
  60% { opacity:1; transform:scale(1.1) } 
  100% { transform:scale(1) } 
}
@keyframes ppStagger { from { opacity:0; transform:translateY(20px) rotateX(10deg) } to { opacity:1; transform:translateY(0) rotateX(0) } }
@keyframes ppSwing { 
  0% { opacity:0; transform:rotate(-8deg) translateY(30px) } 
  50% { transform:rotate(4deg) } 
  100% { opacity:1; transform:rotate(0) translateY(0) } 
}
@keyframes ppElastic { 
  0% { opacity:0; transform:scale(0.5) } 
  55% { transform:scale(1.08) } 
  70% { transform:scale(0.96) } 
  85% { transform:scale(1.02) } 
  100% { opacity:1; transform:scale(1) } 
}
@keyframes ppFloatSubtle { 
  0%, 100% { transform:translateY(0) } 
  50% { transform:translateY(-6px) } 
}
@keyframes ppGlowPulse { 
  0%, 100% { filter: drop-shadow(0 0 8px rgba(99,102,241,0.3)); } 
  50% { filter: drop-shadow(0 0 16px rgba(99,102,241,0.5)); } 
}
.animate-float-subtle { animation: ppFloatSubtle 4s ease-in-out infinite; }
.animate-glow-pulse { animation: ppGlowPulse 3s ease-in-out infinite; }
`;

function getAnimStyle(type, index = 0) {
  if (!type || type === 'none') return {};
  const d = index * 0.12; // Increased stagger delay
  const ease = 'cubic-bezier(0.22, 1, 0.36, 1)';
  const spring = 'cubic-bezier(0.34, 1.56, 0.64, 1)';
  const map = {
    'fade': `ppFade 1s ${d}s ${ease} both`,
    'slide-left': `ppSlideL 0.9s ${d}s ${ease} both`,
    'slide-right': `ppSlideR 0.9s ${d}s ${ease} both`,
    'slide-up': `ppSlideU 0.9s ${d}s ${ease} both`,
    'slide-down': `ppSlideD 0.9s ${d}s ${ease} both`,
    'zoom-in': `ppZoom 0.8s ${d}s ${spring} both`,
    'bounce-in': `ppBounceIn 1s ${d}s ${ease} both`,
    'rotate-in': `ppRotate 0.9s ${d}s ${spring} both`,
    'flip': `ppFlip 1s ${d}s ${ease} both`,
    'flip-x': `ppFlipX 1s ${d}s ${ease} both`,
    'swing': `ppSwing 1s ${d}s ${ease} both`,
    'elastic': `ppElastic 1.2s ${d}s ease-out both`,
    'stagger': `ppStagger 0.8s ${d}s ${ease} both`,
  };
  return { animation: map[type] || `ppEnter 0.8s ${d}s ${ease} both` };
}

function getHeroAnim(type) {
  const ease = 'cubic-bezier(0.22, 1, 0.36, 1)';
  if (!type || type === 'none') return {};
  const map = {
    'fade': `ppFade 1.5s ${ease} both`,
    'slide-up': `ppSlideU 1.2s ${ease} both`,
    'zoom-in': `ppZoom 1.4s ${ease} both`,
  };
  return { animation: map[type] || `ppFade 1.5s ${ease} both` };
}

function AudioPlayer({ url }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const toggle = () => {
    if (!audioRef.current) return;
    playing ? audioRef.current.pause() : audioRef.current.play().catch(() => {});
    setPlaying(!playing);
  };

  if (!url) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50" data-testid="audio-player">
      <audio ref={audioRef} src={url.startsWith('http') ? url : `${BACKEND_URL}${url}`} loop onEnded={() => setPlaying(false)} />
      <button onClick={toggle} data-testid="audio-toggle-btn"
        className="w-14 h-14 rounded-full bg-card/90 backdrop-blur-xl border border-border/50 shadow-2xl flex items-center justify-center text-foreground hover:scale-105 active:scale-95 transition-all duration-200"
        style={{ animation: playing ? 'ppPulseRing 2s ease-out infinite' : 'none' }}>
        {playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
      </button>
    </div>
  );
}

export default function PublicProfile({ prefetchedData }) {
  const { username } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(prefetchedData || null);
  const [loading, setLoading] = useState(!prefetchedData);

  useEffect(() => {
    if (prefetchedData) return;
    const variantId = searchParams.get('v');
    const url = variantId ? `/p/${username}?v=${variantId}` : `/p/${username}`;
    api.get(url)
      .then((res) => {
        if (res.data.suspended) { navigate("/suspended"); return; }
        setData(res.data);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [username, searchParams, navigate, prefetchedData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-10 h-10 border-2 border-primary/40 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!data || !data.profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background text-center p-8">
        <div style={{ animation: 'ppZoom 0.5s cubic-bezier(0.22,1,0.36,1) both' }}>
          <QrCode className="mx-auto text-muted-foreground/40 mb-5" size={48} />
          <h1 className="text-2xl font-bold mb-2 tracking-tight">Profile Not Found</h1>
          <p className="text-muted-foreground text-sm">This Smart-QR profile does not exist or has been removed.</p>
        </div>
      </div>
    );
  }

  const { profile, links, variant } = data;
  const sections = variant?.visible_sections || {
    show_avatar: true, show_title: true, show_description: true, show_links: true, show_cover: true, show_hero: true,
    avatar_offset_x: 0, avatar_offset_y: 0,
  };
  const avatarOffsetX = sections.avatar_offset_x || 0;
  const avatarOffsetY = sections.avatar_offset_y || 0;
  const animType = (variant?.animation && variant?.animation_type && variant.animation_type !== 'none') ? variant.animation_type : null;
  const audioUrl = variant?.audio_url || '';

  const hasContactInfo = !!(profile.phone || profile.company || profile.email_public || profile.website || profile.address ||
    (variant?.contact_overrides && Object.values(variant.contact_overrides).some(v => v)));
  const isContactVariant = variant?.preset === 'contact' || variant?.preset === 'business';

  const handleSaveContact = () => {
    const vcardUrl = variant?.id
      ? `${BACKEND_URL}/api/vcard/${profile.username}?v=${variant.id}`
      : `${BACKEND_URL}/api/vcard/${profile.username}`;
    window.open(vcardUrl, '_blank');
  };

  const handleLinkClick = (link) => {
    api.post("/analytics/link-click", { profile_id: profile.id, link_id: link.id }).catch(() => {});
  };

  const imgSrc = (path) => {
    if (!path) return null;
    return path.startsWith('http') ? path : `${BACKEND_URL}${path}`;
  };

  let idx = 0;
  const nextAnim = () => getAnimStyle(animType, idx++);

  const hasHero = sections.show_hero && profile.hero_media;
  const hasCover = sections.show_cover && profile.cover_image && !hasHero;
  const hasMedia = hasHero || hasCover;

  return (
    <div className="min-h-screen bg-background" data-testid="public-profile-page">
      <SEO
        title={profile.title || profile.username}
        description={profile.description || `Check out ${profile.title || profile.username}'s Smart-QR profile.`}
        ogTitle={profile.title || profile.username}
        ogDescription={profile.description || `Links, contact info, and more from ${profile.title || profile.username}.`}
        ogImage={profile.profile_image ? (profile.profile_image.startsWith('http') ? profile.profile_image : `${BACKEND_URL}${profile.profile_image}`) : undefined}
        ogUrl={`${window.location.origin}/p/${profile.username}`}
      />
      <style>{KEYFRAMES}</style>

      {/* ─── Hero / Cover Media ─── */}
      {hasHero && (
        <div className="relative w-full overflow-hidden" style={getHeroAnim(animType)} data-testid="profile-hero">
          <img src={imgSrc(profile.hero_media)} alt="" className="w-full h-56 sm:h-72 md:h-80 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background" />
        </div>
      )}
      {hasCover && (
        <div className="relative h-44 sm:h-56 md:h-64 w-full overflow-hidden" style={getHeroAnim(animType)} data-testid="profile-cover">
          <img src={imgSrc(profile.cover_image)} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background" />
        </div>
      )}

      <div className={`max-w-md mx-auto px-6 pb-20 ${hasMedia ? '-mt-20 relative z-10' : 'pt-14'}`}>

        {/* ─── Avatar ─── */}
        {sections.show_avatar && (
          <div className="flex justify-center mb-7" style={nextAnim()} data-testid="profile-avatar">
            <div 
              className="animate-float-subtle"
              style={{
                transform: `translate(${avatarOffsetX}px, ${avatarOffsetY}px)`,
                transition: 'transform 0.3s ease-out',
                animationDelay: '1.5s',
              }}
            >
              {profile.profile_image ? (
                <div className="relative group animate-glow-pulse" style={{ animation: 'ppAvatarPop 0.8s cubic-bezier(0.22,1,0.36,1) both, ppGlowPulse 3s ease-in-out 2s infinite' }}>
                  <div className="absolute -inset-1.5 bg-gradient-to-br from-primary/25 to-primary/5 rounded-full blur-md group-hover:blur-lg transition-all duration-500" />
                  <img src={imgSrc(profile.profile_image)} alt={profile.title || 'Profile'}
                    className="relative w-28 h-28 rounded-full object-cover border-[3px] border-background shadow-2xl" />
                </div>
              ) : (
                <div className="relative group" style={{ animation: 'ppAvatarPop 0.8s cubic-bezier(0.22,1,0.36,1) both, ppGlowPulse 3s ease-in-out 2s infinite' }}>
                  <div className="absolute -inset-1.5 bg-gradient-to-br from-primary/25 to-primary/5 rounded-full blur-md" />
                  <div className="relative w-28 h-28 rounded-full bg-gradient-to-br from-primary/15 to-primary/5 border-[3px] border-background shadow-2xl flex items-center justify-center">
                    <span className="text-4xl font-bold text-primary/70">{(profile.title || 'P').charAt(0).toUpperCase()}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── Title ─── */}
        {sections.show_title && (
          <div className="text-center mb-3" style={nextAnim()} data-testid="profile-title-section">
            <h1 className="font-heading text-2xl font-bold tracking-tight leading-tight">{profile.title || 'Untitled Profile'}</h1>
          </div>
        )}

        {/* ─── Description ─── */}
            {sections.show_description && profile.description && (
              <p className="text-center text-muted-foreground text-sm leading-relaxed mb-8 max-w-xs mx-auto" style={nextAnim()} data-testid="profile-description">
                {profile.description}
              </p>
            )}

        {/* ─── Save Contact (prominent on contact variants) ─── */}
        {hasContactInfo && isContactVariant && (
          <div className="mb-8" style={nextAnim()} data-testid="save-contact-section">
            <button
              onClick={handleSaveContact}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl bg-primary text-primary-foreground font-semibold text-sm shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-300"
              data-testid="save-contact-btn"
            >
              <UserPlus size={20} />
              Save Contact
            </button>
          </div>
        )}

        {/* ─── Quick Actions (Website, Email, Phone) ─── */}
        {(profile.website || profile.email_public || profile.phone) && (
          <div className="flex items-center justify-center gap-3 mb-8" style={nextAnim()} data-testid="quick-actions-row">
            {profile.website && (
              <a href={profile.website} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm hover:border-primary/40 hover:bg-card text-xs font-medium transition-all duration-300 hover:-translate-y-0.5"
                data-testid="quick-action-website">
                <Globe size={15} className="text-primary" /> Website
              </a>
            )}
            {profile.email_public && (
              <a href={`mailto:${profile.email_public}`}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm hover:border-primary/40 hover:bg-card text-xs font-medium transition-all duration-300 hover:-translate-y-0.5"
                data-testid="quick-action-email">
                <Mail size={15} className="text-primary" /> Email
              </a>
            )}
            {profile.phone && (
              <a href={`tel:${profile.phone}`}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm hover:border-primary/40 hover:bg-card text-xs font-medium transition-all duration-300 hover:-translate-y-0.5"
                data-testid="quick-action-phone">
                <Phone size={15} className="text-primary" /> Call
              </a>
            )}
          </div>
        )}

        {/* ─── Links ─── */}
        {sections.show_links && links && links.length > 0 && (
          <div className="space-y-3" data-testid="profile-links">
            {links.map((link) => (
              <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer"
                onClick={() => handleLinkClick(link)}
                className="group flex items-center gap-4 px-5 py-4 rounded-2xl border border-border/50 bg-card/80 backdrop-blur-sm hover:border-primary/40 hover:bg-card hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:-translate-y-0.5"
                style={nextAnim()}
                data-testid={`profile-link-${link.id}`}>
                <div className="w-10 h-10 rounded-xl bg-primary/8 flex items-center justify-center text-primary/80 shrink-0 group-hover:bg-primary/12 group-hover:text-primary transition-all duration-300">
                  <LinkIcon icon={link.icon} />
                </div>
                <span className="font-medium text-sm flex-1 truncate">{link.label}</span>
                <ArrowUpRight size={16} className="text-muted-foreground/30 group-hover:text-primary/60 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300 shrink-0" />
              </a>
            ))}
          </div>
        )}

        {/* ─── Save Contact (secondary for non-contact variants) ─── */}
        {hasContactInfo && !isContactVariant && (
          <div className="mt-8" style={nextAnim()} data-testid="save-contact-secondary">
            <button
              onClick={handleSaveContact}
              className="w-full flex items-center justify-center gap-2.5 px-5 py-3.5 rounded-2xl border border-border/50 bg-card/80 backdrop-blur-sm hover:border-primary/40 hover:bg-card hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 hover:-translate-y-0.5 text-sm font-medium"
              data-testid="save-contact-btn"
            >
              <UserPlus size={18} className="text-primary" />
              Save Contact
            </button>
          </div>
        )}

        {/* ─── Variant badge ─── */}
        {variant && (
          <div className="mt-12 text-center" style={nextAnim()}>
            <span className="text-[11px] text-muted-foreground/50 px-3.5 py-1.5 rounded-full border border-border/30 bg-secondary/20 backdrop-blur-sm inline-flex items-center gap-1.5" data-testid="variant-badge">
              <QrCode size={10} />
              {variant.name}
            </span>
          </div>
        )}

        {/* ─── Footer ─── */}
        <p className="text-center text-[10px] text-muted-foreground/25 mt-14 tracking-widest uppercase" data-testid="profile-footer">
          Powered by Smart-QR
        </p>
      </div>

      {audioUrl && <AudioPlayer url={audioUrl} />}
    </div>
  );
}
